import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, User, Menu, X, Search, LogOut, Leaf, Feather } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import CartDrawer from "./CartDrawer";
import PromoBanner from "./PromoBanner";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop All" },
  { to: "/shop?category=essential-oils", label: "Essential Oils" },
  { to: "/shop?category=diy-supplies", label: "DIY Supplies" },
  { to: "/shop?category=garden-gifts", label: "Home, Garden & Gifts" },
  { to: "/recipes", label: "Recipes" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "Our Story" },
  { to: "/track", label: "Track Order" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const { itemCount, setDrawerOpen } = useCart();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [recipeCount, setRecipeCount] = useState(null);
  const [blogCount, setBlogCount] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isSoar = location.pathname === "/soar";

  useEffect(() => {
    api.get("/recipes").then((r) => setRecipeCount(r.data.length)).catch(() => {});
    api.get("/blog").then((r) => setBlogCount(r.data.length)).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) {
      navigate(`/shop?q=${encodeURIComponent(searchQ.trim())}`);
      setSearchOpen(false);
      setSearchQ("");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <PromoBanner />

      {/* Tribute banner — home only, above free shipping */}
      {isHome && (
        <Link
          to="/soar"
          data-testid="soar-tribute-banner"
          className="block bg-[var(--ink-forest)] text-[var(--bg-cream)] hover:bg-[var(--ink-forest)]/95 transition-colors border-b border-[var(--gold)]/20"
        >
          <div className="max-w-6xl mx-auto px-5 sm:px-10 py-3 flex items-center justify-center gap-3 text-center">
            <Feather className="w-4 h-4 text-[var(--gold)] shrink-0" strokeWidth={1.5} />
            <p className="text-xs sm:text-sm tracking-wide">
              <span className="text-[var(--gold)] font-serif italic">In loving memory</span>
              <span className="mx-2 text-[var(--bg-cream)]/40">·</span>
              <span className="text-[var(--bg-cream)]/90">Visit our tribute to Jackie &amp; Dolly</span>
              <span className="ml-2 text-[var(--gold)]">→</span>
            </p>
          </div>
        </Link>
      )}

      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[var(--bg-cream)]/85 backdrop-blur-xl border-b border-[var(--border)]/60">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 2xl:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-baseline gap-1.5 group whitespace-nowrap shrink-0" data-testid="logo-home-link">
              <span className="font-serif text-2xl sm:text-3xl 2xl:text-4xl font-medium tracking-tight text-[var(--ink-forest)] group-hover:text-[var(--terracotta)] transition-colors">
                DC Gift World
              </span>
              <span className="font-serif text-lg sm:text-xl italic text-[var(--terracotta)]">Too</span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden xl:flex items-center gap-4 2xl:gap-6 mx-4">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    `text-[13px] 2xl:text-sm font-medium tracking-wide transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${
                      isActive
                        ? "text-[var(--terracotta)]"
                        : "text-[var(--ink-forest)]/80 hover:text-[var(--terracotta)]"
                    }`
                  }
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                  {link.label === "Recipes" && recipeCount !== null && (
                    <span className="bg-[var(--terracotta)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none" data-testid="nav-recipe-count">
                      {recipeCount}
                    </span>
                  )}
                  {link.label === "Blog" && blogCount !== null && blogCount > 0 && (
                    <span className="bg-[var(--terracotta)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none" data-testid="nav-blog-count">
                      {blogCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-1.5 sm:gap-3">
              <button
                onClick={() => setSearchOpen((s) => !s)}
                className="p-2.5 rounded-full hover:bg-[var(--muted)] transition-colors"
                aria-label="Search"
                data-testid="search-toggle-btn"
              >
                <Search className="w-5 h-5 text-[var(--ink-forest)]" />
              </button>

              {user ? (
                <div className="hidden sm:flex items-center gap-1">
                  <Link
                    to={user.role === "admin" ? "/admin" : "/account"}
                    className="p-2.5 rounded-full hover:bg-[var(--muted)] transition-colors flex items-center gap-2"
                    data-testid="account-link"
                  >
                    <User className="w-5 h-5 text-[var(--ink-forest)]" />
                  </Link>
                  <button
                    onClick={logout}
                    className="p-2.5 rounded-full hover:bg-[var(--muted)] transition-colors"
                    aria-label="Sign out"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-5 h-5 text-[var(--ink-forest)]" />
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="hidden sm:inline-flex p-2.5 rounded-full hover:bg-[var(--muted)] transition-colors"
                  data-testid="login-link"
                >
                  <User className="w-5 h-5 text-[var(--ink-forest)]" />
                </Link>
              )}

              <button
                onClick={() => setDrawerOpen(true)}
                className="relative p-2.5 rounded-full hover:bg-[var(--muted)] transition-colors"
                aria-label="Cart"
                data-testid="cart-toggle-btn"
              >
                <ShoppingBag className="w-5 h-5 text-[var(--ink-forest)]" />
                {itemCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 bg-[var(--terracotta)] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center"
                    data-testid="cart-count-badge"
                  >
                    {itemCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setMobileOpen((m) => !m)}
                className="xl:hidden p-2.5 rounded-full hover:bg-[var(--muted)] transition-colors"
                aria-label="Menu"
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? (
                  <X className="w-5 h-5 text-[var(--ink-forest)]" />
                ) : (
                  <Menu className="w-5 h-5 text-[var(--ink-forest)]" />
                )}
              </button>
            </div>
          </div>

          {/* Search bar */}
          {searchOpen && (
            <form
              onSubmit={handleSearch}
              className="pb-5 flex items-center gap-2"
              data-testid="search-form"
            >
              <input
                autoFocus
                type="text"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search oils, statues, supplies…"
                className="flex-1 bg-[var(--bg-surface)] border border-[var(--border)] rounded-full px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--terracotta)]"
                data-testid="search-input"
              />
              <button
                type="submit"
                className="bg-[var(--terracotta)] text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-[var(--terracotta-deep)] transition-colors"
                data-testid="search-submit-btn"
              >
                Search
              </button>
            </form>
          )}

          {/* Mobile menu */}
          {mobileOpen && (
            <div className="xl:hidden pb-6 border-t border-[var(--border)]/60 -mx-5 sm:-mx-8 px-5 sm:px-8 pt-4">
              <nav className="flex flex-col gap-1" data-testid="mobile-nav">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    end={link.to === "/"}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `py-2.5 text-base font-medium ${
                        isActive ? "text-[var(--terracotta)]" : "text-[var(--ink-forest)]"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
                <div className="border-t border-[var(--border)] mt-3 pt-3 flex flex-col gap-1">
                  {user ? (
                    <>
                      <Link
                        to={user.role === "admin" ? "/admin" : "/account"}
                        onClick={() => setMobileOpen(false)}
                        className="py-2.5 text-base font-medium text-[var(--ink-forest)]"
                      >
                        {user.role === "admin" ? "Admin Panel" : "My Account"}
                      </Link>
                      <button
                        onClick={() => {
                          logout();
                          setMobileOpen(false);
                        }}
                        className="py-2.5 text-base font-medium text-[var(--ink-forest)] text-left"
                      >
                        Sign out
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      onClick={() => setMobileOpen(false)}
                      className="py-2.5 text-base font-medium text-[var(--ink-forest)]"
                    >
                      Sign in
                    </Link>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Free shipping banner — hidden on tribute page for tone */}
      {!isSoar && (
        <div
          className="bg-[var(--ink-forest)] text-[var(--bg-cream)] text-center text-xs sm:text-sm py-2.5 px-4 font-medium tracking-wide"
          data-testid="free-shipping-banner"
        >
          <Leaf className="inline-block w-3.5 h-3.5 mr-2 -mt-0.5 text-[var(--gold)]" />
          Free shipping on orders over $75 · Hand-curated with care
        </div>
      )}

      <main className="flex-1">{children}</main>

      <CartDrawer />

      <Footer />
    </div>
  );
}

function Footer() {
  // Race auto-celebration: after June 20 2026, gracefully shift the badge to "thank you"
  const now = new Date();
  const raceDay = new Date("2026-06-20T16:00:00Z"); // 10am MT
  const postRaceCutoff = new Date("2026-07-05T00:00:00Z"); // 2 weeks of "thanks" after
  const showRace = now < raceDay;
  const showThanks = now >= raceDay && now < postRaceCutoff;

  return (
    <footer className="bg-[var(--ink-forest)] text-[var(--bg-cream)] mt-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <h3 className="font-serif text-3xl mb-3">
              DC Gift World <span className="italic text-[var(--gold)]">Too</span>
            </h3>
            <p className="text-sm text-[var(--bg-cream)]/70 max-w-md leading-relaxed">
              Hand-curated essential oils, DIY craft supplies, and whimsical garden gifts —
              made to bring warmth and wonder home.
            </p>

            {(showRace || showThanks) && (
              <div
                className="mt-7 inline-block bg-[var(--bg-cream)]/5 border border-[var(--gold)]/30 rounded-2xl px-5 py-4 max-w-md"
                data-testid="epawa-sponsor-block"
              >
                <p className="eyebrow text-[var(--gold)] mb-1.5 flex items-center gap-1.5">
                  <span aria-hidden>🐾</span> Proud sponsor
                </p>
                {showRace ? (
                  <>
                    <p className="text-sm text-[var(--bg-cream)]/90 leading-relaxed">
                      <strong className="text-[var(--bg-cream)]">Celebration of Summer 5K</strong> Walk/Run —
                      benefiting <strong>EPAWA</strong> animal welfare.
                    </p>
                    <p className="mt-1.5 text-xs text-[var(--bg-cream)]/70">
                      Sat, June 20 · 10am · Parmer Park, Limon CO · Pet-friendly 🐶
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-[var(--bg-cream)]/90 leading-relaxed">
                    Thank you to everyone who ran with us on June 20 — every paw, every
                    pair of sneakers. <strong>EPAWA</strong> & the animals thank you. 💛
                  </p>
                )}
              </div>
            )}
          </div>
          <div>
            <p className="eyebrow text-[var(--gold)] mb-4">Shop</p>
            <ul className="space-y-2 text-sm text-[var(--bg-cream)]/70">
              <li><Link to="/shop?category=essential-oils" className="hover:text-[var(--gold)]">Essential Oils</Link></li>
              <li><Link to="/shop?category=diy-supplies" className="hover:text-[var(--gold)]">DIY Supplies</Link></li>
              <li><Link to="/shop?category=garden-gifts" className="hover:text-[var(--gold)]">Home, Garden &amp; Gifts</Link></li>
            </ul>
          </div>
          <div>
            <p className="eyebrow text-[var(--gold)] mb-4">Help</p>
            <ul className="space-y-2 text-sm text-[var(--bg-cream)]/70">
              <li><Link to="/about" className="hover:text-[var(--gold)]">Our Story</Link></li>
              <li><Link to="/recipes" className="hover:text-[var(--gold)]">Recipes</Link></li>
              <li><Link to="/blog" className="hover:text-[var(--gold)]">Blog</Link></li>
              <li><Link to="/safety" className="hover:text-[var(--gold)]">Safety Guide</Link></li>
              <li><Link to="/track" className="hover:text-[var(--gold)]">Track Order</Link></li>
              <li><Link to="/login" className="hover:text-[var(--gold)]">Sign In</Link></li>
              <li><Link to="/contact" className="hover:text-[var(--gold)]" data-testid="footer-contact-link">Contact Us</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-[var(--bg-cream)]/15 text-xs text-[var(--bg-cream)]/50 flex justify-between flex-wrap gap-3">
          <span>© {new Date().getFullYear()} DC Gift World Too. All rights reserved.</span>
          <span>Free shipping over $75 · Made with care</span>
        </div>
      </div>
    </footer>
  );
}
